// =======================================
// DATA TYPES
// =======================================

/**
 * Project data structure (includes both projects and stages)
 */
export interface ProjectOption {
  id: number | string;
  job_number: number | bigint;
  display_name: string;
  status?: string;
  entity_type?: 'project' | 'stage';
  parent_project_id?: number;
  stage_id?: string;
  stage_description?: string;
  parent_description?: string;
  client_company_name?: string;
  client_id?: number;
  job_type?: 'Design' | 'Advisory' | 'RWC' | 'RWD';
}

/**
 * Props interface for SearchableProjectDropdown
 */
export interface SearchableProjectDropdownProps {
  /** Currently selected project ID */
  value?: string | number | null;
  /**
   * What to show for the selected value instead of looking the id up in
   * `projects`. Required when the id may be a stage's: project and stage ids
   * overlap, so a lookup by id alone can name the wrong job.
   */
  displayLabel?: string | null;
  /** Callback when project selection changes */
  onChange: (projectId: string | number | null, project?: ProjectOption | null) => void;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Additional CSS classes */
  className?: string;
  /** Whether the dropdown is disabled */
  disabled?: boolean;
  /** Whether to show a "No Project" option */
  allowNone?: boolean;
  /** Custom label for the "No Project" option */
  noneLabel?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Whether to show loading state */
  showLoading?: boolean;
  /**
   * Restrict results to projects or to stages. The backing search returns
   * both; omitted, both are offered (the Timesheets behaviour).
   */
  entityType?: 'project' | 'stage';
  /**
   * Characters before a search fires. Default 3 (MIN_SEARCH_LENGTH); the
   * timesheet add rows pass 1 so a short job number finds its project.
   */
  minSearchLength?: number;
  /**
   * Fixed choices listed ahead of the search results, always when the menu
   * opens and filtered by the typed text once there is any: the timesheet
   * add rows list the leave types here so "leave" is one control, not two.
   * Picking one calls onChange(item.id, null).
   */
  leadingItems?: LeadingItem[];
}

export interface LeadingItem {
  id: string;
  label: string;
}

/**
 * Search response from the backend
 */
export interface ProjectSearchResponse {
  projects: ProjectOption[];
  total_count: number;
  has_more: boolean;
}

// =======================================
// CONSTANTS AND OPTIONS
// =======================================

/**
 * Minimum characters required to trigger search
 */
export const MIN_SEARCH_LENGTH = 3;

/**
 * Debounce delay for search input in milliseconds
 */
export const SEARCH_DEBOUNCE_DELAY = 300;

/**
 * Maximum number of projects to fetch per search
 */
export const MAX_SEARCH_RESULTS = 50;

/**
 * Default placeholder text
 */
export const DEFAULT_PLACEHOLDER = "Search projects...";

/**
 * Default "No Project" option
 */
export const NO_PROJECT_OPTION = {
  id: 'none',
  job_number: 0,
  display_name: 'No Project',
  label: 'No Project',
  value: 'none'
} as const; 